import { useEffect, useState } from 'preact/hooks';
import { ApiError, type Api } from '../api.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { ConfirmDialog, Dialog, MoreOptions, Pill, toast } from '../ui.js';

export interface AbsenceRequestView {
  id: string;
  from: string;
  to: string;
  halfDayStart: boolean;
  halfDayEnd: boolean;
  status: 'queued' | 'sent' | 'approved' | 'declined' | 'withdrawn' | 'failed';
  error: string | null;
}

type Status = AbsenceRequestView['status'];
const TONE: Record<Status, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = { queued: 'neutral', sent: 'info', approved: 'ok', declined: 'crit', withdrawn: 'neutral', failed: 'warn' };

const TX = texts(
  {
    status: { queued: 'Sending…', sent: 'Away · requested', approved: 'Away · approved', declined: 'Declined', withdrawn: 'Withdrawn', failed: "Couldn't send" } as Record<Status, string>,
    afternoon: ' (afternoon)',
    morning: ' (morning)',
    halfDays: ' (half days)',
    withdrawnToast: (p: string) => `Request for ${p} withdrawn`,
    sentToast: (p: string) => `Request for ${p} sent to Personio`,
    sendFailed: "Couldn't send – try again or register it in Personio",
    title: 'Absence',
    request: 'Request absence',
    infoOn: 'Requests go to Personio, where your manager approves them as today.',
    infoOff: 'Register absence in Personio – it is not connected to the Hub yet.',
    retry: 'Retry',
    withdraw: 'Withdraw request',
    withdrawQ: (p: string) => `Withdraw your request for ${p}?`,
    withdrawBody: 'The request is withdrawn in Personio too. To be away those days again you send a new request.',
    changeInPersonio: 'Change it in Personio',
    dialogSub: "Sent to Personio for approval. The type is only passed on to Personio – the Hub doesn't keep it.",
    type: 'Type',
    choose: 'Choose…',
    loading: 'Loading…',
    from: 'From',
    to: 'To',
    halfDay: 'Half day',
    afternoonOnly: 'Afternoon only',
    firstAfternoon: 'First day: afternoon only',
    morningOnly: 'Morning only',
    lastMorning: 'Last day: morning only',
    comment: 'Comment to the approver (optional)',
    cancel: 'Cancel',
    send: 'Send request'
  },
  {
    status: { queued: 'Skickas…', sent: 'Ledig · väntar på godkännande', approved: 'Ledig · godkänd', declined: 'Avslagen', withdrawn: 'Tillbakadragen', failed: 'Kunde inte skickas' },
    afternoon: ' (eftermiddag)',
    morning: ' (förmiddag)',
    halfDays: ' (halvdagar)',
    withdrawnToast: (p: string) => `Ansökan för ${p} är tillbakadragen`,
    sentToast: (p: string) => `Ansökan för ${p} skickad till Personio`,
    sendFailed: 'Kunde inte skicka – försök igen eller registrera den i Personio',
    title: 'Frånvaro',
    request: 'Ansök om frånvaro',
    infoOn: 'Ansökningar går till Personio, där din chef godkänner dem som i dag.',
    infoOff: 'Registrera frånvaro i Personio – det är inte kopplat till Hubben än.',
    retry: 'Försök igen',
    withdraw: 'Dra tillbaka ansökan',
    withdrawQ: (p: string) => `Dra tillbaka ansökan för ${p}?`,
    withdrawBody: 'Ansökan dras tillbaka i Personio också. Vill du vara ledig de dagarna skickar du en ny ansökan.',
    changeInPersonio: 'Ändra den i Personio',
    dialogSub: 'Skickas till Personio för godkännande. Typen skickas bara vidare till Personio – Hubben sparar den inte.',
    type: 'Typ',
    choose: 'Välj…',
    loading: 'Laddar…',
    from: 'Från',
    to: 'Till',
    halfDay: 'Halvdag',
    afternoonOnly: 'Bara eftermiddag',
    firstAfternoon: 'Första dagen: bara eftermiddag',
    morningOnly: 'Bara förmiddag',
    lastMorning: 'Sista dagen: bara förmiddag',
    comment: 'Kommentar till den som godkänner (valfritt)',
    cancel: 'Avbryt',
    send: 'Skicka ansökan'
  }
);

/** The request's dates, e.g. "2 Nov – 6 Nov" or in Swedish "2 nov – 6 nov". */
export const period = (r: Pick<AbsenceRequestView, 'from' | 'to' | 'halfDayStart' | 'halfDayEnd'>, lang: Lang = 'en') => {
  const t = TX[lang];
  const day = lang === 'sv' ? dmSv : dm;
  const half = r.from === r.to ? (r.halfDayStart ? t.afternoon : r.halfDayEnd ? t.morning : '') : r.halfDayStart || r.halfDayEnd ? t.halfDays : '';
  return (r.from === r.to ? day(r.from) : `${day(r.from)} – ${day(r.to)}`) + half;
};

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** My Work › Absence (UX-1064): *Request absence* for employees, sent to Personio for approval. */
export function AbsenceBlock(p: { api: Api; today: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<AbsenceRequestView[] | null>(null);
  const [connected, setConnected] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<AbsenceRequestView | null>(null);
  const load = () =>
    p.api.get<{ requests: AbsenceRequestView[]; personio: boolean }>('/api/absence/requests').then(
      (r) => {
        setRows(r.requests);
        setConnected(r.personio);
      },
      () => setRows([])
    );
  useEffect(() => {
    void load();
  }, []);
  const act = async (r: AbsenceRequestView, what: 'retry' | 'withdraw') => {
    setBusy(r.id);
    try {
      const out = await p.api.post<AbsenceRequestView>(`/api/absence/requests/${r.id}/${what}`);
      toast({ text: what === 'withdraw' ? t.withdrawnToast(period(r, lang)) : t.sentToast(period(out, lang)) });
    } catch (e) {
      toast({ text: e instanceof ApiError && e.status === 502 && what === 'retry' ? t.sendFailed : msg(e) });
    } finally {
      setBusy(null);
      void load();
    }
  };
  // not connected to Personio and nothing requested: no box that only says so (owner 2026-11-01)
  if (!connected && !rows?.length) return null;
  return (
    <section class="blk" aria-labelledby="home-absence">
      <div class="blk-head">
        <h2 class="blk-title" id="home-absence">
          {t.title}
        </h2>
        {connected ? (
          <div class="blk-actions">
            <button type="button" class="btn" onClick={() => setOpen(true)}>
              {t.request}
            </button>
          </div>
        ) : null}
      </div>
      <p class="blk-info">{connected ? t.infoOn : t.infoOff}</p>
      {rows && rows.length ? (
        <ul class="list">
          {rows.map((r) => (
            <li class="list-row">
              <span>
                {period(r, lang)} <Pill tone={TONE[r.status]}>{t.status[r.status]}</Pill>
                {r.status === 'failed' && r.error ? <span class="small muted"> {r.error.replace(/^[a-z_]+: /, '')}</span> : null}
              </span>
              <span>
                {r.status === 'failed' ? (
                  <button type="button" class="btn btn-sm" disabled={busy === r.id} onClick={() => void act(r, 'retry')}>
                    {t.retry}
                  </button>
                ) : null}
                {r.status === 'sent' || r.status === 'failed' || r.status === 'queued' ? (
                  <button type="button" class="btn btn-ghost btn-sm" disabled={busy === r.id} onClick={() => setWithdrawing(r)}>
                    {t.withdraw}
                  </button>
                ) : r.status === 'approved' ? (
                  <span class="small muted">{t.changeInPersonio}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {withdrawing ? (
        <ConfirmDialog
          title={t.withdrawQ(period(withdrawing, lang))}
          body={<p>{t.withdrawBody}</p>}
          confirmLabel={t.withdraw}
          danger
          onConfirm={() => act(withdrawing, 'withdraw')}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}
      {open ? (
        <RequestDialog
          api={p.api}
          today={p.today}
          onClose={() => setOpen(false)}
          onSent={() => {
            setOpen(false);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

const VACATION = /semester|vacation|holiday/i;

export function RequestDialog(p: { api: Api; today: string; onClose: () => void; onSent: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [types, setTypes] = useState<{ id: string; name: string }[] | null>(null);
  const [typesErr, setTypesErr] = useState<string | null>(null);
  const [typeId, setTypeId] = useState('');
  const [from, setFrom] = useState(p.today);
  const [to, setTo] = useState(p.today);
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [comment, setComment] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    p.api.get<{ types: { id: string; name: string }[] }>('/api/absence/types').then(
      (r) => {
        setTypes(r.types);
        // the usual one is preselected: the only type, else vacation
        setTypeId((r.types.length === 1 ? r.types[0] : r.types.find((x) => VACATION.test(x.name)))?.id ?? '');
      },
      (e) => setTypesErr(msg(e))
    );
  }, []);
  const oneDay = from === to;
  const send = async () => {
    setErrs({});
    setBusy(true);
    try {
      const r = await p.api.post<AbsenceRequestView>('/api/absence/requests', { typeId, from, to, halfDayStart: halfStart, halfDayEnd: oneDay ? halfEnd && !halfStart : halfEnd, comment });
      toast({ text: t.sentToast(period(r, lang)) });
      p.onSent();
    } catch (e) {
      const d = e instanceof ApiError ? (e.details as { errors?: Record<string, string> } | undefined) : undefined;
      if (e instanceof ApiError && e.status === 502) {
        toast({ text: t.sendFailed });
        p.onSent();
      } else setErrs(d?.errors ?? { form: msg(e) });
    } finally {
      setBusy(false);
    }
  };
  const inv = (k: string) => (errs[k] ? { 'aria-invalid': true as const, 'aria-describedby': `abs-${k}-err` } : {});
  const err = (k: string) =>
    errs[k] ? (
      <span class="field-error" id={`abs-${k}-err`}>
        {errs[k]}
      </span>
    ) : null;
  return (
    <Dialog
      title={t.request}
      sub={t.dialogSub}
      onClose={p.onClose}
      onSubmit={() => void send()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={busy || !types}>
            {t.send}
          </button>
        </>
      }
    >
        <div class="field">
          <label class="label" for="abs-type">
            {t.type}
          </label>
          {typesErr ? (
            <p class="field-error" role="alert">
              {typesErr}
            </p>
          ) : (
            <select class="input input--wide" id="abs-type" value={typeId} disabled={!types} onChange={(e) => setTypeId((e.target as HTMLSelectElement).value)} {...inv('typeId')}>
              <option value="">{types ? t.choose : t.loading}</option>
              {(types ?? []).map((t) => (
                <option value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          {err('typeId')}
        </div>
        <div class="cluster">
          <div class="field">
            <label class="label" for="abs-from">
              {t.from}
            </label>
            <input
              class="input input--date"
              type="date"
              id="abs-from"
              value={from}
              onInput={(e) => {
                const v = (e.target as HTMLInputElement).value;
                setFrom(v);
                if (v && to < v) setTo(v);
              }}
              {...inv('from')}
            />
            {err('from')}
          </div>
          <div class="field">
            <label class="label" for="abs-to">
              {t.to}
            </label>
            <input class="input input--date" type="date" id="abs-to" value={to} min={from} onInput={(e) => setTo((e.target as HTMLInputElement).value)} {...inv('to')} />
            {err('to')}
          </div>
        </div>
        <MoreOptions id="absence" open={Boolean(errs.comment)} filled={(halfStart || halfEnd ? 1 : 0) + (comment.trim() ? 1 : 0)}>
          <fieldset class="field fieldset">
            <legend class="label">{t.halfDay}</legend>
            <label class="check">
              <input type="checkbox" checked={halfStart} onChange={(e) => setHalfStart((e.target as HTMLInputElement).checked)} /> {oneDay ? t.afternoonOnly : t.firstAfternoon}
            </label>
            <label class={`check${oneDay && halfStart ? ' is-disabled' : ''}`}>
              <input type="checkbox" checked={halfEnd && !(oneDay && halfStart)} disabled={oneDay && halfStart} onChange={(e) => setHalfEnd((e.target as HTMLInputElement).checked)} /> {oneDay ? t.morningOnly : t.lastMorning}
            </label>
          </fieldset>
          <div class="field">
            <label class="label" for="abs-comment">
              {t.comment}
            </label>
            <textarea class="textarea" id="abs-comment" style="min-height: 80px" maxLength={500} value={comment} onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)} {...inv('comment')} />
            {err('comment')}
          </div>
        </MoreOptions>
        {errs.form ? (
          <p class="field-error" role="alert">
            {errs.form}
          </p>
        ) : null}
    </Dialog>
  );
}
